import { useMemo, useState } from 'react';
import type { CopyListing } from '@/lib/api';
import { classNames } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Search } from 'lucide-react';

interface Props {
  anuncios: CopyListing[];
  optimizados: Set<number>;
  seleccionado: number | null;
  cargando: boolean;
  onSeleccionar: (id: number) => void;
}

const SIN_LOCALIDAD = 'Sin localidad';

function nombreVisible(a: CopyListing): string {
  return a.nickname || a.name || `Anuncio #${a.id}`;
}

/**
 * Panel izquierdo con todos los anuncios de Hostify.
 *
 * El nickname manda sobre el `name`: es el nombre interno con el que el equipo
 * conoce cada piso, y es lo único de la ficha que esta sección NO edita nunca.
 */
export default function ListaAnuncios({
  anuncios, optimizados, seleccionado, cargando, onSeleccionar,
}: Props) {
  const [busqueda, setBusqueda] = useState('');
  const [plegadas, setPlegadas] = useState<Set<string>>(new Set());

  const grupos = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const filtrados = q
      ? anuncios.filter(a =>
          nombreVisible(a).toLowerCase().includes(q) ||
          String(a.id).includes(q) ||
          (a.city || '').toLowerCase().includes(q))
      : anuncios;

    const porCiudad = new Map<string, CopyListing[]>();
    for (const a of filtrados) {
      const ciudad = a.city || SIN_LOCALIDAD;
      const lista = porCiudad.get(ciudad);
      if (lista) lista.push(a);
      else porCiudad.set(ciudad, [a]);
    }

    return [...porCiudad.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ciudad, lista]) => ({
        ciudad,
        lista: [...lista].sort((a, b) => nombreVisible(a).localeCompare(nombreVisible(b))),
      }));
  }, [anuncios, busqueda]);

  const total = anuncios.length;
  const numOptimizadas = anuncios.filter(a => optimizados.has(a.id)).length;
  const pendientes = total - numOptimizadas;

  const alternarCiudad = (ciudad: string) => {
    setPlegadas(prev => {
      const siguiente = new Set(prev);
      if (siguiente.has(ciudad)) siguiente.delete(ciudad);
      else siguiente.add(ciudad);
      return siguiente;
    });
  };

  return (
    <div className="card p-0 overflow-hidden flex flex-col flex-1 min-h-0">
      <div className="p-4 border-b border-slate-200 dark:border-white/10">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
          <input
            type="text"
            placeholder="Buscar por nombre, ID o ciudad..."
            className="input pl-9"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs">
          <span className="flex items-center gap-1 text-slate-500">
            <CheckCircle2 size={13} className="text-green-600" />
            {numOptimizadas}/{total} optimizadas
          </span>
          {pendientes > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertCircle size={13} />
              {pendientes} pendientes
            </span>
          )}
        </div>
      </div>

      <div className="overflow-y-auto flex-1">
        {cargando ? (
          <LoadingSpinner />
        ) : grupos.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8 px-4">
            {total === 0 ? 'No hay anuncios' : 'Ningún anuncio coincide con la búsqueda'}
          </p>
        ) : (
          grupos.map(({ ciudad, lista }) => {
            const abierta = !plegadas.has(ciudad);
            return (
              <div key={ciudad}>
                <button
                  type="button"
                  onClick={() => alternarCiudad(ciudad)}
                  className="w-full flex items-center gap-1.5 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200 dark:border-white/10 hover:bg-slate-100 transition-colors"
                >
                  {abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="flex-1 truncate normal-case tracking-normal text-slate-600">{ciudad}</span>
                  <span className="badge bg-gray-100 text-gray-700">{lista.length}</span>
                </button>

                {abierta && lista.map(a => {
                  const activo = a.id === seleccionado;
                  const optimizada = optimizados.has(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onSeleccionar(a.id)}
                      className={classNames(
                        'w-full flex items-center gap-2 px-4 py-2.5 text-left border-b border-slate-200 dark:border-white/10 transition-colors',
                        activo ? 'bg-sereno-blue/50' : 'hover:bg-sereno-blue/20',
                      )}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">{nombreVisible(a)}</span>
                        <span className="block text-xs text-slate-500">ID: {a.id}</span>
                      </span>
                      {optimizada ? (
                        <CheckCircle2 size={16} className="text-green-600 shrink-0" aria-label="Textos optimizados" />
                      ) : (
                        <AlertCircle size={16} className="text-amber-500 shrink-0" aria-label="Pendiente de optimizar" />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
